import {
  AttributedData,
  CPTBRow,
  DashboardData,
  DecayRow,
  HierarchyLevel,
  SaturationRow
} from '../../dtc-commerce-mmm-types';
import {
  getAggregatedMediaType,
  getParentChannelKey,
  getSubChannelLabel
} from '../../utils/dtc-commerce-mmm-channel-helpers';
import { formatFieldName } from '../../hooks/useChannelAggregation';
import {
  ContributionsByType,
  DecChannelData,
  SatChannelData,
  SubChannelInfo,
  TSPoint
} from './channel-drawer-types';

interface DrawerDataInputs {
  data: DashboardData;
  attributedData: AttributedData;
  selectedGroupKey: string;
  clickedLevel: HierarchyLevel;
  isRevenue: boolean;
}

export interface DrawerData {
  parentChannelKey: string;
  parentDisplayName: string;
  parentMediaType: string;
  parentIsDark: boolean;
  subChannels: SubChannelInfo[];
  channelTimeSeries: TSPoint[];
  subChannelTimeSeries: Map<string, TSPoint[]>;
  saturationData: SatChannelData[];
  decayData: DecChannelData[];
  contributionsByType: ContributionsByType;
  subChannelContributions: Map<string, ContributionsByType>;
  totalSpend: number;
  totalEffect: number;
  totalEfficiency: number | null;
}

// ROAS for revenue models (contribution per $ of spend), CPA otherwise (spend
// per conversion). Null when the denominator is zero.
const computeEfficiency = (isRevenue: boolean, spend: number, effect: number): number | null => {
  if (isRevenue) return spend > 0 ? effect / spend : null;
  return effect > 0 ? spend / effect : null;
};

export const deriveDrawerData = ({
  data,
  attributedData,
  selectedGroupKey,
  clickedLevel,
  isRevenue
}: DrawerDataInputs): DrawerData => {
  const parentChannelKey = getParentChannelKey(selectedGroupKey, clickedLevel, attributedData.cptb);

  const byName = new Map<string, CPTBRow[]>();
  attributedData.cptbT12M.forEach(row => {
    if (row.channel !== parentChannelKey) return;
    const bucket = byName.get(row.name) ?? [];
    bucket.push(row);
    byName.set(row.name, bucket);
  });

  const subChannels: SubChannelInfo[] = [];
  byName.forEach((rows, name) => {
    const first = rows[0];
    const rawSpend = rows.reduce((s, r) => s + r.rawSpend, 0);
    const effectiveSpend = rows.reduce((s, r) => s + r.effectiveSpend, 0);
    const effectiveNormalized = rows.reduce((s, r) => s + r.effectiveNormalized, 0);
    const chInfo = data.channelHierarchy.channels.find(c => c.name === name);
    const roasRow = attributedData.roas.find(r => r.name === name) ?? null;
    const efficiency = computeEfficiency(isRevenue, effectiveSpend, effectiveNormalized);
    subChannels.push({
      name,
      channel: first.channel,
      platform: first.platform,
      tactic: first.tactic,
      brandNonbrand: first.brandNonbrand,
      mediaType: first.mediaType,
      incrementalityTest: first.incrementalityTest,
      isDark: chInfo?.isDark ?? false,
      rawSpend,
      effectiveSpend,
      effectiveNormalized,
      efficiency,
      roas: roasRow
    });
  });
  subChannels.sort((a, b) => b.effectiveNormalized - a.effectiveNormalized);

  const parentDisplayName = formatFieldName(parentChannelKey);
  const parentMediaType = getAggregatedMediaType(subChannels.map(sc => sc.mediaType));
  const parentIsDark = subChannels.length > 0 && subChannels.every(sc => sc.isDark);

  const tsByDate = new Map<string, TSPoint>();
  attributedData.cptb.forEach(row => {
    if (row.channel !== parentChannelKey) return;
    const cur = tsByDate.get(row.date) ?? { date: row.date, spend: 0, contribution: 0 };
    cur.spend += row.rawSpend;
    cur.contribution += row.effectiveNormalized;
    tsByDate.set(row.date, cur);
  });
  const channelTimeSeries = Array.from(tsByDate.values()).sort((a, b) =>
    a.date.localeCompare(b.date)
  );

  const subChannelTimeSeries = new Map<string, TSPoint[]>();
  const byNameDate = new Map<string, Map<string, TSPoint>>();
  attributedData.cptb.forEach(row => {
    if (row.channel !== parentChannelKey) return;
    const dateMap = byNameDate.get(row.name) ?? new Map<string, TSPoint>();
    const cur = dateMap.get(row.date) ?? { date: row.date, spend: 0, contribution: 0 };
    cur.spend += row.rawSpend;
    cur.contribution += row.effectiveNormalized;
    dateMap.set(row.date, cur);
    byNameDate.set(row.name, dateMap);
  });
  byNameDate.forEach((dateMap, name) => {
    subChannelTimeSeries.set(
      name,
      Array.from(dateMap.values()).sort((a, b) => a.date.localeCompare(b.date))
    );
  });

  const nameSet = new Set(subChannels.map(sc => sc.name));

  const spendByName = new Map<string, number>();
  const weeksByName = new Map<string, Set<string>>();
  attributedData.cptbT12M.forEach(row => {
    if (!nameSet.has(row.name)) return;
    spendByName.set(row.name, (spendByName.get(row.name) ?? 0) + row.rawSpend);
    const weeks = weeksByName.get(row.name) ?? new Set<string>();
    weeks.add(row.date);
    weeksByName.set(row.name, weeks);
  });

  const satByName = new Map<string, SaturationRow[]>();
  attributedData.saturation
    .filter(r => nameSet.has(r.name))
    .forEach(r => {
      const bucket = satByName.get(r.name) ?? [];
      bucket.push(r);
      satByName.set(r.name, bucket);
    });
  const saturationData: SatChannelData[] = Array.from(satByName.entries()).map(([name, rows]) => {
    const sorted = rows.sort((a, b) => a.effectiveAdstockSpend - b.effectiveAdstockSpend);
    const first = sorted[0];
    const label = getSubChannelLabel(
      {
        platform: first.platform,
        tactic: first.tactic,
        brandNonbrand: first.brandNonbrand,
        name: first.name
      },
      'channel'
    );
    const totalSpend = spendByName.get(name) ?? 0;
    const nWeeks = weeksByName.get(name)?.size ?? 0;
    const weeklyAvgSpend = nWeeks > 0 ? totalSpend / nWeeks : null;
    return {
      name,
      label,
      rows: sorted,
      weeklyAvgSpend,
      channel: first.channel,
      platform: first.platform,
      mediaType: first.mediaType
    };
  });

  const decByName = new Map<string, DecayRow[]>();
  data.decay
    .filter(r => nameSet.has(r.name))
    .forEach(r => {
      const bucket = decByName.get(r.name) ?? [];
      bucket.push(r);
      decByName.set(r.name, bucket);
    });
  const decayData: DecChannelData[] = Array.from(decByName.entries()).map(([name, rows]) => {
    const chInfo = data.channelHierarchy.channels.find(c => c.name === name);
    const label = chInfo
      ? getSubChannelLabel(
          {
            platform: chInfo.platform,
            tactic: chInfo.tactic,
            brandNonbrand: chInfo.brandNonbrand,
            name
          },
          'channel'
        )
      : name;
    return {
      name,
      label,
      rows: rows.sort((a, b) => a.period - b.period),
      channel: chInfo?.channel ?? '',
      platform: chInfo?.platform ?? '',
      mediaType: chInfo?.mediaType ?? ''
    };
  });

  const sumByType = (cptb: CPTBRow[], mediaType: string): number =>
    cptb
      .filter(r => r.channel === parentChannelKey && r.mediaType === mediaType)
      .reduce((s, r) => s + r.effectiveNormalized, 0);
  const contributionsByType: ContributionsByType = {
    demandGen: {
      direct: sumByType(data.raw.cptbT12M, 'demand_generation'),
      actualized: sumByType(data.actualized.cptbT12M, 'demand_generation')
    },
    demandCap: {
      direct: sumByType(data.raw.cptbT12M, 'demand_capture'),
      actualized: sumByType(data.actualized.cptbT12M, 'demand_capture')
    }
  };

  const subChannelContributions = new Map<string, ContributionsByType>();
  subChannels.forEach(sc => {
    const info = data.channelHierarchy.channels.find(c => c.name === sc.name);
    const mt = info?.mediaType ?? '';
    const sumForName = (cptb: CPTBRow[]): number =>
      cptb.filter(r => r.name === sc.name).reduce((s, r) => s + r.effectiveNormalized, 0);
    const rawVal = sumForName(data.raw.cptbT12M);
    const actVal = sumForName(data.actualized.cptbT12M);
    subChannelContributions.set(sc.name, {
      demandGen:
        mt === 'demand_generation'
          ? { direct: rawVal, actualized: actVal }
          : { direct: 0, actualized: 0 },
      demandCap:
        mt === 'demand_capture'
          ? { direct: rawVal, actualized: actVal }
          : { direct: 0, actualized: 0 }
    });
  });

  const totalSpend = subChannels.reduce((s, sc) => s + sc.rawSpend, 0);
  const totalEffect = subChannels.reduce((s, sc) => s + sc.effectiveNormalized, 0);
  const totalEffSpend = subChannels.reduce((s, sc) => s + sc.effectiveSpend, 0);
  const totalEfficiency = computeEfficiency(isRevenue, totalEffSpend, totalEffect);

  return {
    parentChannelKey,
    parentDisplayName,
    parentMediaType,
    parentIsDark,
    subChannels,
    channelTimeSeries,
    subChannelTimeSeries,
    saturationData,
    decayData,
    contributionsByType,
    subChannelContributions,
    totalSpend,
    totalEffect,
    totalEfficiency
  };
};
